import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, User, Mail, Calendar, Download, Trash2, Edit2, Check, X, Crown, AlertTriangle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import ThemeToggle from '../components/ThemeToggle';
import apiService from '../services/api';
import BrandLogo from '../components/BrandLogo';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await apiService.getProfile();
      setProfile(data);
      setEditName(data.name);
    } catch (err) {
      console.error('Error fetching profile:', err);
      // Redirect to home if not authenticated
      if (err.response?.status === 401 || err.message?.includes('401') || err.message?.includes('Authentication')) {
        navigate('/');
      } else {
        // For any other error, also redirect to home (user likely not logged in)
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!editName.trim()) return;
    
    try {
      setSaving(true);
      await apiService.updateProfile({ name: editName.trim() });
      setProfile(prev => ({ ...prev, name: editName.trim() }));
      setIsEditing(false);
      
      // Update localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        user.name = editName.trim();
        localStorage.setItem('user', JSON.stringify(user));
      }
    } catch (err) {
      console.error('Error updating name:', err);
      alert('Failed to update name. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadData = async () => {
    try {
      setDownloading(true);
      const data = await apiService.downloadMyData();
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kaicheckit_data_${profile.user_id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading data:', err);
      alert('Failed to download data. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      await apiService.deleteAccount();
      
      // Clear local storage and redirect
      localStorage.removeItem('user');
      navigate('/', { state: { accountDeleted: true } });
    } catch (err) {
      console.error('Error deleting account:', err);
      alert('Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const isPremium = profile?.subscription_tier && ['pro', 'premium', 'enterprise'].includes(profile.subscription_tier);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getProviderLabel = (provider) => {
    switch (provider) {
      case 'google': return 'Google';
      case 'facebook': return 'Facebook';
      case 'email': return 'Email';
      default: return provider;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <div className="text-slate-600 dark:text-slate-300">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-4">
        <Alert className="max-w-md bg-red-50 dark:bg-red-900/40 border-red-200 dark:border-red-700">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <AlertDescription className="text-red-700 dark:text-red-300">{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto space-y-6">
          
          {/* Page Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              My Profile
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Manage your account and data
            </p>
          </div>

          {/* Personal Information */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-slate-800 dark:text-slate-100">
                <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Name */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-500 dark:text-slate-400 w-20">Name</span>
                  {isEditing ? (
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-48"
                      maxLength={100}
                    />
                  ) : (
                    <span className="text-slate-800 dark:text-slate-100 font-medium">{profile?.name}</span>
                  )}
                </div>
                {isEditing ? (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setIsEditing(false); setEditName(profile?.name); }}
                      disabled={saving}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveName}
                      disabled={saving || !editName.trim()}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Email */}
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500 dark:text-slate-400 w-16">Email</span>
                <span className="text-slate-800 dark:text-slate-100">{profile?.email}</span>
              </div>

              {/* Login Provider */}
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500 dark:text-slate-400 w-16">Login</span>
                <span className="text-slate-800 dark:text-slate-100">{getProviderLabel(profile?.provider)}</span>
              </div>

              {/* Created Date */}
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500 dark:text-slate-400 w-16">Joined</span>
                <span className="text-slate-800 dark:text-slate-100">{formatDate(profile?.created_at)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Status */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-slate-800 dark:text-slate-100">
                <Crown className={`w-5 h-5 ${isPremium ? 'text-purple-600' : 'text-slate-400'}`} />
                Subscription
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant={isPremium ? "default" : "secondary"} className={isPremium ? "bg-purple-600" : ""}>
                    {isPremium ? "Premium" : "Free"}
                  </Badge>
                  {!isPremium && (
                    <span className="text-sm text-slate-500 dark:text-slate-400">5 checks/month</span>
                  )}
                </div>
                {!isPremium && (
                  <Button
                    onClick={() => navigate('/pricing')}
                    size="sm"
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white"
                  >
                    Upgrade
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* GDPR Data Rights */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-slate-800 dark:text-slate-100">
                <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
                Your Data Rights
              </CardTitle>
              <CardDescription className="dark:text-slate-400">
                Under GDPR, you have the right to access, export, and delete your data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Download Data */}
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div>
                  <h4 className="font-medium text-slate-800 dark:text-slate-100">Download My Data</h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {isPremium 
                      ? "Export your personal info and verification history as JSON"
                      : "Export your personal info as JSON (upgrade for history)"
                    }
                  </p>
                </div>
                <Button
                  onClick={handleDownloadData}
                  variant="outline"
                  disabled={downloading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloading ? 'Downloading...' : 'Download'}
                </Button>
              </div>

              {/* Delete Account */}
              <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div>
                  <h4 className="font-medium text-red-800 dark:text-red-200">Delete My Account</h4>
                  <p className="text-sm text-red-600 dark:text-red-300">
                    Permanently delete your account and all data
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" disabled={deleting}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="dark:bg-slate-800">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                        <AlertTriangle className="w-5 h-5" />
                        Delete Account Permanently?
                      </AlertDialogTitle>
                      <AlertDialogDescription className="dark:text-slate-300">
                        This action cannot be undone. This will permanently delete:
                        <ul className="list-disc ml-6 mt-2 space-y-1">
                          <li>Your account and profile information</li>
                          <li>All your verification history</li>
                          <li>Your subscription (if any)</li>
                        </ul>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="dark:bg-slate-700 dark:text-slate-100">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        className="bg-red-600 hover:bg-red-700"
                        disabled={deleting}
                      >
                        {deleting ? 'Deleting...' : 'Yes, Delete My Account'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Privacy Policy Link */}
              <div className="text-center pt-2">
                <a 
                  href="/privacy" 
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Read our Privacy Policy →
                </a>
              </div>
            </CardContent>
          </Card>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <BrandLogo variant="plain" data-testid="brand-logo-footer" />
              <span className="text-slate-600 dark:text-slate-400">© 2025 Kaiventive Holdings</span>
            </div>
            <div className="flex gap-6">
              <a href="/privacy" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Privacy Policy</a>
              <a href="/cookies" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Cookie Policy</a>
              <a href="/terms" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProfilePage;
